/**
 * Surety bond request letters — pure helpers.
 *
 * Prefills a request from the won deal + timber project, computes the bond
 * amount, and produces the letter copy the PDF prints. No firebase imports.
 */

import type { Address, EnhancedDeal, TimberProject } from '../types';
import {
  DEFAULT_SURETY_ENCLOSURES,
  SURETY_BOND_TYPE_LABELS,
  type SuretyAgent,
  type SuretyBondRequest,
  type SuretyBondType,
  type SuretyBondingSettings,
  type SuretyContractRange,
  type SuretySigner,
} from '../types/regulatory';
import { getProjectDisplayName } from './projectName';
import { LCTF_DEFAULT_CEO_NAME, LCTF_DEFAULT_CEO_TITLE, LCTF_OFFER_COMPANY, LCTF_OFFER_LEGAL } from './jobOfferLetter';

export const LCTF_TN_CONTRACTOR_LICENSE = 'TN Contractor License #78475';

export const DEFAULT_SURETY_AGENT: SuretyAgent = {
  agency: '',
  contact: '',
  email: '',
  phone: '',
  address: '',
};

export const DEFAULT_SURETY_SIGNER: SuretySigner = {
  name: LCTF_DEFAULT_CEO_NAME,
  title: LCTF_DEFAULT_CEO_TITLE,
};

export const DEFAULT_SURETY_BONDING_SETTINGS: SuretyBondingSettings = {
  agent: DEFAULT_SURETY_AGENT,
  signer: DEFAULT_SURETY_SIGNER,
  defaultBidBondPercent: 10,
  defaultWarrantyMonths: 12,
  defaultBondForm: 'AIA A312',
};

/** Hover help — what the surety does with each field on the request form. */
export const SURETY_FIELD_HELP = {
  project: 'The job the bond covers. Everything below prefills from the deal and its Hub project; fix anything the surety should see differently.',
  bondType: 'Bid bond: guarantees we will sign the contract and provide final bonds if awarded, usually 5–10% of the bid. Performance & Payment: guarantee we finish the work and pay subs and suppliers, each 100% of the contract.',
  letterDate: 'Date printed on the letter. The surety files the request under this date.',
  contractAmount: 'Bid amount for a bid bond, or the signed contract price including approved change orders for performance and payment bonds. The surety sizes the bond (and its premium) from this number.',
  contractRange: 'Before the client signs, the price can still move with their selections — structure arrangement, optional add-ons, species, installation. Quote the low and high so the surety can underwrite to the top; the bond amount is sized from the high end.',
  bondPercent: 'Share of the contract amount each bond must cover. The penal sum printed on the bond is contract amount × this percent.',
  bondAmount: 'Penal sum of each bond — the most the surety could pay out. Premium is a percent of this.',
  bidDate: 'Date bids are due. The bid bond must be in the bid package, so the agent works back from this.',
  contractDate: 'Date the contract was (or will be) executed. Performance and payment bonds are dated to it.',
  neededBy: 'When the executed bonds must be in our hands — usually the contract date or the owner\'s notice-to-proceed deadline.',
  startDate: 'When work on site or in the shop begins. Sureties use the window from start to completion to gauge exposure.',
  completionDate: 'Substantial completion date from the contract or schedule. The bond stays open until then plus the warranty.',
  warrantyMonths: 'Warranty period after completion. Many bond forms extend coverage through it, so the surety needs it to price the bond.',
  obligeeRole: 'Who the bond protects. The owner when we hold the prime contract; the general contractor when we are a subcontractor.',
  obligeeName: 'Exact legal name of the owner or GC as it appears on the contract. It is printed on the bond and must match.',
  obligeeAddress: 'Mailing address of the obligee for the bond form.',
  siteAddress: 'Where the frame is installed. Sureties check jurisdiction and whether the project is public or private.',
  architectEngineer: 'Architect or engineer of record. Some bond forms and most public owners list them.',
  bondForm: 'Form the obligee requires — AIA A312 is the private-work standard; public owners usually mandate a statutory or owner form. Tells the agent which paper to issue.',
  scopeOfWork: 'One or two sentences describing our work. The surety confirms it matches what we normally do and what the contract says.',
  designInScope: 'Whether design is part of our contract. Design liability changes how the surety underwrites the job.',
  subcontractedPercent: 'Rough share of the contract we subcontract. A high number raises payment-bond exposure.',
  retainagePercent: 'Retainage the owner holds from each payment. Retainage is security for the surety, so it helps the request.',
  liquidatedDamagesPerDay: 'Daily penalty for late completion in the contract, if any. Sureties price this risk and may ask for the clause.',
  enclosures: 'Documents attached behind the letter. Build enclosures generates the project schedule and contract summary; add anything else the surety asked for.',
  notes: 'Anything else the agent should know — owner deadlines, prior bonds on the job, unusual contract terms.',
  // Settings
  agent: 'The bond agent or broker who places our bonds with the surety. Every letter is addressed to them.',
  signer: 'Who signs the request on LCTF\'s behalf — printed in the signature block.',
  defaultBidBondPercent: 'Starting percent for new bid bond requests; 10% is typical when the bid documents do not say.',
  defaultWarrantyMonths: 'Starting warranty period for new requests.',
  defaultBondForm: 'Starting bond form for new requests.',
} as const;

export type SuretyFieldHelpKey = keyof typeof SURETY_FIELD_HELP;

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function positiveNumber(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Fill gaps in a stored settings doc with defaults. */
export function normalizeSuretyBondingSettings(raw: unknown): SuretyBondingSettings {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const agent = (data.agent && typeof data.agent === 'object' ? data.agent : {}) as Record<string, unknown>;
  const signer = (data.signer && typeof data.signer === 'object' ? data.signer : {}) as Record<string, unknown>;
  return {
    agent: {
      agency: str(agent.agency),
      contact: str(agent.contact),
      email: str(agent.email),
      phone: str(agent.phone),
      address: str(agent.address),
    },
    signer: {
      name: str(signer.name) || DEFAULT_SURETY_SIGNER.name,
      title: str(signer.title) || DEFAULT_SURETY_SIGNER.title,
    },
    defaultBidBondPercent: positiveNumber(data.defaultBidBondPercent, DEFAULT_SURETY_BONDING_SETTINGS.defaultBidBondPercent),
    defaultWarrantyMonths: positiveNumber(data.defaultWarrantyMonths, DEFAULT_SURETY_BONDING_SETTINGS.defaultWarrantyMonths),
    defaultBondForm: str(data.defaultBondForm) || DEFAULT_SURETY_BONDING_SETTINGS.defaultBondForm,
  };
}

export function ymdLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function parseYmdLocal(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

type DateLike = Date | { toDate?: () => Date } | string | number | null | undefined;

function toDate(value: DateLike): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function ymdOrNull(value: DateLike): string | null {
  const d = toDate(value);
  return d ? ymdLocal(d) : null;
}

/** Street / City, ST ZIP as two lines. Falls back to the free-text location. */
export function formatAddress(address: Address | null | undefined, fallback = ''): string {
  if (!address) return fallback.trim();
  const street = (address.street || '').trim();
  const city = (address.city || '').trim();
  const state = (address.state || '').trim();
  const zip = (address.zipCode || '').trim();
  const cityLine = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const lines = [street, cityLine].filter(Boolean);
  return lines.length ? lines.join('\n') : fallback.trim();
}

export function computeBondAmount(contractAmount: number, bondPercent: number): number {
  const contract = Number(contractAmount) || 0;
  const percent = Number(bondPercent) || 0;
  if (contract <= 0 || percent <= 0) return 0;
  return Math.round(contract * percent) / 100;
}

export function defaultBondPercent(bondType: SuretyBondType, settings: SuretyBondingSettings): number {
  return bondType === 'bid' ? settings.defaultBidBondPercent : 100;
}

export function defaultScopeOfWork(projectLabel: string, designInScope: boolean): string {
  const label = projectLabel.trim() || 'the project';
  return designInScope
    ? `Design, fabrication, delivery, and installation of the timber frame for ${label}.`
    : `Fabrication, delivery, and installation of the timber frame for ${label}.`;
}

export type SuretyBondRequestDraft = Omit<
  SuretyBondRequest,
  'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'sentAt' | 'issuedAt'
>;

export function buildSuretyBondRequestDraft(input: {
  deal: EnhancedDeal;
  project: TimberProject | null | undefined;
  settings: SuretyBondingSettings;
  bondType?: SuretyBondType;
  today?: Date;
}): SuretyBondRequestDraft {
  const { deal, project, settings } = input;
  const today = input.today ?? new Date();
  const bondType: SuretyBondType = input.bondType ?? 'performance_payment';
  const projectLabel = getProjectDisplayName(deal) || project?.name || deal.title || 'Project';
  const contractAmount = Math.round((Number(deal.value) || Number(project?.value) || 0) * 100) / 100;
  const bondPercent = defaultBondPercent(bondType, settings);
  const siteAddress = formatAddress(project?.address ?? deal.address, project?.location || deal.location || '');
  const obligeeName = (deal.company || '').trim() || (deal.contactName || '').trim() || (project?.clientName || '').trim();

  return {
    dealId: deal.id || '',
    projectId: project?.id || '',
    projectLabel,
    projectNumber: (deal.projectNumber || '').trim(),
    bondType,
    status: 'draft',
    letterDate: ymdLocal(today),
    contractAmount,
    bondPercent,
    bondAmount: computeBondAmount(contractAmount, bondPercent),
    bidDate: null,
    contractDate: ymdOrNull(project?.contractedAt),
    startDate: ymdOrNull(project?.startDate),
    completionDate: ymdOrNull(project?.targetEndDate),
    warrantyMonths: settings.defaultWarrantyMonths,
    obligee: {
      role: 'owner',
      name: obligeeName,
      address: siteAddress,
    },
    siteAddress,
    scopeOfWork: defaultScopeOfWork(projectLabel, true),
    bondForm: settings.defaultBondForm,
    neededBy: null,
    retainagePercent: null,
    liquidatedDamagesPerDay: null,
    architectEngineer: '',
    subcontractedPercent: null,
    designInScope: true,
    enclosures: [...DEFAULT_SURETY_ENCLOSURES],
    notes: '',
    agent: { ...settings.agent },
    signer: { ...settings.signer },
    bondNumber: '',
    suretyName: '',
  };
}

export function formatBondMoney(amount: number): string {
  const n = Number(amount) || 0;
  const hasCents = Math.abs(n % 1) > 0.004;
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  });
}

export function formatBondPercent(percent: number): string {
  const n = Number(percent) || 0;
  return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
}

export function longDateFromYmd(value: string | null | undefined, fallback = '—'): string {
  const d = parseYmdLocal(value);
  return d ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : fallback;
}

/** The range to print, or null when it is missing, degenerate, or inconsistent with the contract amount. */
export function activeContractRange(
  request: Pick<SuretyBondRequest, 'contractAmount'> & Partial<Pick<SuretyBondRequest, 'contractRange'>>,
): SuretyContractRange | null {
  const r = request.contractRange;
  if (!r) return null;
  const min = Number(r.min);
  const max = Number(r.max);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max <= min) return null;
  return { min, max, basis: (r.basis || '').trim() };
}

/** "$X – $Y" for the details table and list. */
export function formatBondMoneyRange(range: SuretyContractRange): string {
  return `${formatBondMoney(range.min)} – ${formatBondMoney(range.max)}`;
}

export interface SuretyBondRequestLetterCopy {
  reLine: string;
  salutation: string;
  paragraphs: string[];
  enclosuresLine: string | null;
  closing: string;
}

/** Letter body as plain paragraphs — tested here, printed by the PDF. */
export function suretyBondRequestLetter(
  request: Pick<
    SuretyBondRequest,
    | 'bondType' | 'projectLabel' | 'projectNumber' | 'contractAmount' | 'bondPercent' | 'bondAmount'
    | 'bidDate' | 'contractDate' | 'startDate' | 'completionDate' | 'warrantyMonths' | 'obligee'
    | 'siteAddress' | 'scopeOfWork' | 'bondForm' | 'neededBy' | 'enclosures' | 'agent'
  > & Partial<Pick<SuretyBondRequest, 'contractRange'>>,
): SuretyBondRequestLetterCopy {
  const typeLabel = SURETY_BOND_TYPE_LABELS[request.bondType];
  const projectRef = request.projectNumber && !request.projectLabel.includes(request.projectNumber)
    ? `${request.projectLabel} (Project ${request.projectNumber})`
    : request.projectLabel;
  const money = formatBondMoney;
  const percent = formatBondPercent(request.bondPercent);
  const obligeeRole = request.obligee.role === 'general_contractor' ? 'general contractor' : 'owner';
  const obligee = request.obligee.name.trim() || `the ${obligeeRole}`;
  const site = request.siteAddress.trim().replace(/\s*\n\s*/g, ', ');

  const contact = request.agent.contact.trim();
  const salutation = contact ? `Dear ${contact},` : 'To whom it may concern,';

  const paragraphs: string[] = [];
  paragraphs.push(
    `${LCTF_OFFER_LEGAL} requests that your office arrange ${typeLabel.toLowerCase()} on our behalf as principal for ${projectRef}${site ? ` located at ${site}` : ''}. ${obligee} is the ${obligeeRole} and obligee.`,
  );

  const range = activeContractRange(request);
  const rangeWhy = range?.basis ? ` depending on the owner's final selection of ${range.basis}` : " depending on the owner's final selections";
  if (request.bondType === 'bid') {
    if (range) {
      paragraphs.push(
        `Our bid will fall between ${money(range.min)} and ${money(range.max)}${rangeWhy}. The bid documents call for a bid bond of ${percent} of the bid amount; please size the bond to the top of that range, ${money(request.bondAmount)}${request.bidDate ? `, due with our bid on ${longDateFromYmd(request.bidDate)}` : ''}. If awarded, we expect the contract to require performance and payment bonds for the full contract price.`,
      );
    } else {
      paragraphs.push(
        `Our bid is ${money(request.contractAmount)}. The bid documents call for a bid bond of ${percent} of the bid amount, ${money(request.bondAmount)}${request.bidDate ? `, due with our bid on ${longDateFromYmd(request.bidDate)}` : ''}. If awarded, we expect the contract to require performance and payment bonds for the full contract price.`,
      );
    }
  } else if (range) {
    paragraphs.push(
      `The contract amount will fall between ${money(range.min)} and ${money(range.max)}${rangeWhy}${request.contractDate ? `, with the contract to be executed ${longDateFromYmd(request.contractDate)}` : ''}. The contract requires performance and payment bonds, each for ${percent} of the final contract price; please underwrite to the top of the range, ${money(request.bondAmount)} each, and we will confirm the final amount once selections are made.`,
    );
  } else {
    paragraphs.push(
      `The contract amount is ${money(request.contractAmount)}${request.contractDate ? `, executed ${longDateFromYmd(request.contractDate)}` : ''}. The contract requires performance and payment bonds, each in the amount of ${percent} of the contract price, ${money(request.bondAmount)}.`,
    );
  }

  const schedule: string[] = [];
  if (request.startDate) schedule.push(`work is scheduled to begin ${longDateFromYmd(request.startDate)}`);
  if (request.completionDate) schedule.push(`substantial completion is scheduled for ${longDateFromYmd(request.completionDate)}`);
  const scope = request.scopeOfWork.trim();
  const scheduleSentence = schedule.length
    ? `${schedule.join(' and ')}${request.warrantyMonths > 0 ? `, followed by a ${request.warrantyMonths}-month warranty period` : ''}.`
    : request.warrantyMonths > 0
      ? `The contract carries a ${request.warrantyMonths}-month warranty period.`
      : '';
  const scopeBody = scope.replace(/\.$/, '');
  const scopeSentence = scopeBody
    ? `Our scope is ${scopeBody.charAt(0).toLowerCase()}${scopeBody.slice(1)}.`
    : '';
  const combined = [scopeSentence, scheduleSentence ? scheduleSentence.charAt(0).toUpperCase() + scheduleSentence.slice(1) : '']
    .filter(Boolean)
    .join(' ');
  if (combined) paragraphs.push(combined);

  const form = request.bondForm.trim();
  const needed = request.neededBy ? longDateFromYmd(request.neededBy) : null;
  const formParts: string[] = [];
  if (form) formParts.push(`The obligee requires the bonds on ${form} form with the surety's power of attorney attached.`);
  if (needed) formParts.push(`We need the executed bonds in hand by ${needed}.`);
  formParts.push('Please let us know if the surety needs anything further to underwrite this request.');
  paragraphs.push(formParts.join(' '));

  const enclosures = request.enclosures.map((e) => e.trim()).filter(Boolean);

  return {
    reLine: `Re: ${typeLabel} — ${projectRef}`,
    salutation,
    paragraphs,
    enclosuresLine: enclosures.length ? `Enclosures: ${enclosures.join('; ')}` : null,
    closing: 'Thank you for your help with this project.',
  };
}

export function suretyBondRequestPdfFilename(projectLabel: string, letterDate: string): string {
  const safe = projectLabel.replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim() || 'Project';
  const date = parseYmdLocal(letterDate) ? letterDate : ymdLocal(new Date());
  return `Surety Bond Request — ${safe} — ${date}.pdf`;
}

/** Required fields before a letter can be saved. Returns human-readable problems. */
export function validateSuretyBondRequest(draft: SuretyBondRequestDraft): string[] {
  const problems: string[] = [];
  if (!draft.dealId) problems.push('Pick a project.');
  if (!draft.letterDate || !parseYmdLocal(draft.letterDate)) problems.push('Letter date is required.');
  if (!(draft.contractAmount > 0)) problems.push('Contract amount must be greater than zero.');
  if (draft.contractRange) {
    const { min, max } = draft.contractRange;
    if (!(min > 0) || !(max > min)) problems.push('Contract range needs a low value above zero and a high value above the low.');
    else if (Math.abs(max - draft.contractAmount) > 0.5) problems.push('Contract amount must equal the top of the contract range.');
  }
  if (!(draft.bondPercent > 0)) problems.push('Bond percent must be greater than zero.');
  if (!draft.obligee.name.trim()) problems.push('Obligee name is required.');
  if (draft.bondType === 'bid' && !draft.bidDate) problems.push('Bid date is required for a bid bond.');
  if (draft.bondType === 'performance_payment') {
    if (!draft.contractDate) problems.push('Contract date is required for performance and payment bonds.');
    if (!draft.completionDate) problems.push('Substantial completion date is required for performance and payment bonds.');
  }
  return problems;
}

export const SURETY_COMPANY_NAME = LCTF_OFFER_COMPANY;
